import type { PayloadOf, Resolution, TypedEvent } from '$domain/events';
import type { Reducer } from '$domain/reducers/reduce';

/**
 * Catches, and how they were resolved.
 *
 * **Unresolved Catches are a first-class case, not an edge case** (architecture
 * §2.2). A user may log a Catch and close the app; spec §5 says that is a
 * complete rep of skill one. So this reducer never assumes a `catch.resolved`
 * follows a `catch.logged`, and the Comeback distribution is computed over
 * *resolved* catches with the unresolved count reported separately — never
 * silently folded into a denominator, where it would quietly read as failure.
 */

export interface CatchRecord {
  catchId: string;
  /** ISO 8601 with offset — the user's local time, which is what days mean. */
  occurredAt: string;
  triggerTag: string;
  resolution: Resolution | null;
  planId: string | null;
}

export interface CatchesState {
  /** In log order, which is chronological order. */
  readonly all: readonly CatchRecord[];
  readonly byId: ReadonlyMap<string, CatchRecord>;
}

const EMPTY: CatchesState = { all: [], byId: new Map() };

export const catchesReducer: Reducer<CatchesState> = {
  name: 'catches',
  initial: EMPTY,
  handles: ['catch.logged', 'catch.resolved'],
  apply(state, event) {
    if (event.type === 'catch.logged') {
      const payload = event.payload as PayloadOf<'catch.logged'>;
      const record: CatchRecord = {
        catchId: payload.catchId,
        occurredAt: event.occurredAt,
        triggerTag: payload.triggerTag,
        resolution: null,
        planId: null,
      };
      const byId = new Map(state.byId);
      byId.set(record.catchId, record);
      return { all: [...state.all, record], byId };
    }

    const payload = (event as TypedEvent<'catch.resolved'>).payload as PayloadOf<'catch.resolved'>;
    const existing = state.byId.get(payload.catchId);
    // A resolution for a Catch we have never seen. On one device that is a bug;
    // across two synced devices it is a log that arrived out of order, so it is
    // ignored rather than treated as a new Catch — inventing a Catch from a
    // resolution would inflate the count that matters most.
    if (!existing) return state;

    // First resolution wins. Two devices can resolve the same Catch offline,
    // and the union of their logs then holds two answers to a question the user
    // only answered once. "First in log order" is deterministic — every device
    // replaying the same events reaches the same state — and it is the honest
    // reading: the Comeback happened at the earlier moment. There is no
    // correction flow in v1, so a later resolution is a duplicate, not a
    // revision.
    if (existing.resolution !== null) return state;

    const updated: CatchRecord = {
      ...existing,
      resolution: payload.resolution,
      planId: payload.planId ?? null,
    };
    const byId = new Map(state.byId);
    byId.set(updated.catchId, updated);
    return {
      all: state.all.map((c) => (c.catchId === updated.catchId ? updated : c)),
      byId,
    };
  },
};

/** Catches on the given local calendar day, newest first. */
export function catchesOn(state: CatchesState, day: Date): CatchRecord[] {
  const key = localDayKey(day);
  return state.all.filter((c) => localDayKeyFromIso(c.occurredAt) === key).reverse();
}

export function resolvedCount(state: CatchesState): number {
  return state.all.filter((c) => c.resolution !== null).length;
}

export function unresolvedCount(state: CatchesState): number {
  return state.all.length - resolvedCount(state);
}

/**
 * The local calendar day of a timestamp, taken from its own offset.
 *
 * Not `new Date(iso).getDate()`: that reinterprets the instant in the *reader's*
 * current zone, so a Catch logged at 23:30 in Berlin becomes yesterday's when
 * the same log is read on a device in New York. The day a Catch belongs to is
 * the day it was for the person who logged it.
 */
export function localDayKeyFromIso(iso: string): string {
  return iso.slice(0, 10);
}

export function localDayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
